// Copyright (c) 2025 - Present. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted
import { Tooltip, Typography, TypographyProps } from '@mui/material';

interface Props extends Omit<TypographyProps, 'children'> {
  /**
   * The duration in minutes to display
   */
  minutes: number;
}

/**
 * Displays a duration in HH:MM format (no zero padding for hours) with a tooltip
 * showing the expanded format.
 * Examples: 90 minutes -> "1:30" with tooltip "1 hour 30 minutes"
 */
export function MinuteDuration(props: Props) {
  const { minutes, ...typographyProps } = props;

  const hours = Math.floor(minutes / 60);
  const remainingMinutes = minutes % 60;
  const formattedMinutes = remainingMinutes.toString().padStart(2, '0');

  // Create tooltip text
  const getTooltipText = () => {
    const hourText = hours === 1 ? 'hour' : 'hours';
    const minuteText = remainingMinutes === 1 ? 'minute' : 'minutes';

    if (hours > 0 && remainingMinutes > 0) {
      return `${hours} ${hourText} ${remainingMinutes} ${minuteText}`;
    } else if (hours > 0) {
      return `${hours} ${hourText}`;
    }
    return `${remainingMinutes} ${minuteText}`;
  };

  return (
    <Tooltip arrow title={getTooltipText()}>
      <Typography
        {...typographyProps}
        sx={{
          textDecoration: 'underline',
          textDecorationStyle: 'dotted',
          cursor: 'help',
          ...typographyProps.sx,
        }}
      >
        {hours}:{formattedMinutes}
      </Typography>
    </Tooltip>
  );
}
